import { FC, useMemo, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { InfoBar } from '@/components/ui/info-bar';
import { useCurrentFeed } from '@/hooks/use-current-feed';
import { UnitFeedItem } from '@/types/unit-feed-item';
import { FeedList } from './feed-list';

type FeedFilter = UnitFeedItem['kind'];

const feedFilters: { kind: FeedFilter; label: string }[] = [
  { kind: 'wellbeing', label: 'Wellbeing' },
  { kind: 'notification', label: 'Notifications' },
  { kind: 'call', label: 'Calls' },
];

const getErrorMessage = (responsesError: boolean, callsError: boolean) => {
  if (responsesError && callsError) {
    return "Couldn't load feed items for this unit. Please try again.";
  }

  if (responsesError) {
    return "Couldn't load notification and wellbeing responses. Calls are still shown.";
  }

  if (callsError) {
    return "Couldn't load calls. Notification and wellbeing responses are still shown.";
  }

  return null;
};

export const FeedView: FC = () => {
  const { callsError, data, isFetching, isInitialLoading, responsesError } =
    useCurrentFeed();
  const [activeFilters, setActiveFilters] = useState<Set<FeedFilter>>(
    new Set(),
  );
  const errorMessage = getErrorMessage(responsesError, callsError);
  const visibleItems = useMemo(
    () =>
      activeFilters.size === 0 || activeFilters.size === feedFilters.length
        ? data
        : data.filter(({ kind }) => activeFilters.has(kind)),
    [activeFilters, data],
  );

  const toggleFilter = (filter: FeedFilter) => {
    setActiveFilters((currentFilters) => {
      const nextFilters = new Set(currentFilters);

      if (nextFilters.has(filter)) {
        nextFilters.delete(filter);
      } else {
        nextFilters.add(filter);
      }

      return nextFilters;
    });
  };

  return (
    <Card className="w-full p-6">
      <div className="flex items-center gap-3 px-6 pt-1 pb-7">
        <div
          aria-label="Filter feed by item type"
          className="flex flex-wrap gap-2"
          role="group"
        >
          {feedFilters.map(({ kind, label }) => (
            <Button
              aria-pressed={activeFilters.has(kind)}
              key={kind}
              onClick={() => toggleFilter(kind)}
              type="button"
              variant="tertiary"
            >
              {label}
            </Button>
          ))}
        </div>
        {isFetching && !isInitialLoading ? (
          <div aria-label="Refreshing feed" role="status">
            <BeatLoader size={6} />
          </div>
        ) : null}
      </div>
      {errorMessage ? (
        <InfoBar className="mb-6" text={errorMessage} variant="error" />
      ) : null}
      <FeedList
        items={visibleItems}
        loading={isInitialLoading}
        showEmptyState={!errorMessage}
      />
    </Card>
  );
};
